import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { getBudgetsForClient, selectClientBudgets, selectLoadingClientBudgets } from "./ClientBudgetsTableSlice";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import { faMoneyBillTransfer, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { ClientPortalBudgetModel } from "services/api/cplapi/CPLAPI";

const ClientTransactionsTable: React.FC = () => {
  const { clientID } = useParams();
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const clientBudgets = useSelector(selectClientBudgets);
  const isLoading = useSelector(selectLoadingClientBudgets);

  const columnHelper = createColumnHelper<ClientPortalBudgetModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: () => (
        <div className="d-flex">
          <FontAwesomeIcon icon={faMoneyBillTransfer} size="lg" className="cpl-brand-primary" />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.budgetYear, {
      id: "Budget Year",
      cell: (info) => info.getValue(),
      header: () => <>Budget Year</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.annualBudget, {
      id: "Annual Budget",
      cell: (info) => info.getValue()?.toGBPString(),
      header: () => <>Annual Budget</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.accessor((row) => row.actualToDate, {
      id: "Actual To Date",
      cell: (info) => info.getValue()?.toGBPString(),
      header: () => <>Actual To Date</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <div
          data-test-id={`budgets-table__view-budget-link-${info.row.original.budgetID}`}
          className="cpl-btn btn-sm cpl-btn-outline"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="View Budget"
          onClick={() => {
            navigateToBudget(info.row.original);
          }}
        >
          <FontAwesomeIcon icon={faArrowRight} size="lg" />
        </div>
      ),
      enableHiding: false,
    }),
  ];

  useEffect(() => {
    thunkDispatch(getBudgetsForClient({ clientID: clientID! }));
  }, [thunkDispatch, clientID]);

  const navigateToBudget = (row: ClientPortalBudgetModel) => {
    const path = `/ClientBudgets/${clientID}/${row.budgetID}`;
    navigate(path);
  };

  return (
    <>
      <div className="card d-flex">
        <ActivityIndicator isBusy={isLoading}>
          <div className="card-body">
            <CPLTable
              data={clientBudgets.budgets ?? []}
              columns={columnDefinition}
              rowMobileClickAction={(row) => navigateToBudget(row)}
              sortingState={[{ id: "Budget Year", desc: true }]}
              classNames="cpl-table-nowrap-below-desktop"
            />
          </div>
        </ActivityIndicator>
      </div>
    </>
  );
};

export default ClientTransactionsTable;
